import { useState } from "react";
import { products, categories } from "@/data/products";
import ProductCard from "@/components/ProductCard";
import ProductModal from "@/components/ProductModal";

// Feature-grouped lists for browsing by flavour family (NOT category)
const flavourGroups = [
  { name: "Real Fruit", filter: (p) => /real|fruit punch|fresh fruits/i.test(p.name) || p.badges?.some(b => /real fruit/i.test(b)) },
  { name: "Indian Heritage", filter: (p) => /kulfi|matka|gulab|punjabi|kesar|mava|raj bhog|rabri|sitaphal|anjeer/i.test(p.name) },
  { name: "Chocolate Lovers", filter: (p) => /chocolate|chocobar|choco|brownie|cassata|black forest/i.test(p.name) },
  { name: "Tropical & Fruity", filter: (p) => /mango|pineapple|orange|litchi|jack fruit|custard|fruit/i.test(p.name) && !/real/i.test(p.name) },
  { name: "Kids' Picks", filter: (p) => /tooty|tutti|cherry|merry|dolly|kiti|double decker|coca/i.test(p.name) },
];

const featuredIds = ["kulfi-pista","fp-real-mango","fp-american-nuts","cone-chocolate","other-shahi-matka","fp-kesar-pista"];

export default function Flavours() {
  const [open, setOpen] = useState(null);

  return (
    <section className="py-12 md:py-16" data-testid="flavours-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs tracking-[0.2em] uppercase text-holiday-red font-bold">Flavours</p>
        <h1 className="font-display text-5xl md:text-7xl leading-[0.95] mt-3">{products.length}+ flavours.<br/><span className="font-serif italic">One scoop at a time.</span></h1>

        <p className="mt-6 max-w-2xl text-choc/80 leading-relaxed">From 26-year-old kulfi recipes to seasonal real-fruit experiments — pick your flavour family, then choose the size: kulfi, candy, cup, cone, family or bulk.</p>

        {/* Featured */}
        <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6" data-testid="flavours-featured-grid">
          {featuredIds.map(id => products.find(p => p.id === id)).filter(Boolean).map(p => (
            <ProductCard key={p.id} product={p} onOpen={setOpen}/>
          ))}
        </div>

        {/* Stats strip */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ["8", "Categories"],
            [products.filter(p => p.category !== "bulk").length + "", "Retail SKUs"],
            [products.filter(p => p.category === "bulk").length + "", "Bulk packs"],
            ["24×7", "Always open"],
          ].map(([n, l]) => (
            <div key={l} className="bg-white rounded-2xl p-5 border border-[#F0E4E4] cushion text-center">
              <div className="font-display text-4xl text-holiday-red">{n}</div>
              <div className="text-xs uppercase tracking-widest text-choc/70 mt-1">{l}</div>
            </div>
          ))}
        </div>

        {/* Flavour family groups */}
        <div className="mt-20 space-y-12">
          {flavourGroups.map((g, idx) => {
            const items = products.filter(g.filter);
            if (!items.length) return null;
            const tints = ["bg-ice-pink","bg-mint","bg-sun-yellow-soft","bg-white","bg-ice-pink"];
            return (
              <div key={g.name} className={`${tints[idx]} rounded-[2.5rem] p-6 md:p-12 cushion border border-[#F0E4E4]`} data-testid={`flavour-group-${g.name.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'')}`}>
                <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                  <h2 className="font-display text-3xl md:text-4xl">{g.name}</h2>
                  <span className="text-xs uppercase tracking-widest font-bold text-holiday-red">{items.length} flavours</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                  {items.slice(0, 10).map(p => <ProductCard key={p.id} product={p} onOpen={setOpen}/>)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Browse by category strip */}
        <div className="mt-20 bg-choc text-cream rounded-[2.5rem] p-8 md:p-12 cushion" data-testid="browse-by-category">
          <h2 className="font-display text-3xl md:text-4xl">Or browse by size & format</h2>
          <p className="font-serif italic text-cream/70 mt-2">{categories.length} categories · {products.length} SKUs</p>
          <div className="mt-6 flex flex-wrap gap-3">
            {categories.map(c => (
              <a key={c.id} href={`/products`} className="bg-cream text-choc px-5 py-2.5 rounded-full text-sm font-bold hover:bg-holiday-red hover:text-white transition-colors" data-testid={`category-pill-${c.id}`}>
                {c.label} <span className="opacity-60 ml-1 text-xs">{c.priceHint}</span>
              </a>
            ))}
          </div>
        </div>
      </div>

      <ProductModal product={open} onClose={() => setOpen(null)} />
    </section>
  );
}

